import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Circle, Pentagon, MapPin, Loader2, Gauge, Cpu } from "lucide-react";
import {
  fetchGeofences,
  createGeofence,
  updateGeofence,
  deleteGeofence,
  type Geofence,
} from "@/lib/traccar-api";
import { GeofenceMap } from "@/components/geofences/GeofenceMap";
import { GeofenceDevicesDialog } from "@/components/geofences/GeofenceDevicesDialog";

type GeofenceType = "circle" | "polygon";

const DEFAULT_COLOR = "#3b82f6";

interface GeofenceFormData {
  name: string;
  description: string;
  type: GeofenceType;
  radius: number; // For circles, in meters
  coordinates: [number, number][]; // For polygons or circle center
  maxSpeed: number | "";
  color: string;
}

const parseWKT = (area: string): { type: GeofenceType; center?: [number, number]; radius?: number; coordinates?: [number, number][] } => {
  if (area.startsWith("CIRCLE")) {
    // CIRCLE (lat lon, radius)
    const match = area.match(/CIRCLE\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*,\s*([-\d.]+)\s*\)/i);
    if (match) {
      return {
        type: "circle",
        center: [parseFloat(match[2]), parseFloat(match[1])], // [lng, lat]
        radius: parseFloat(match[3]),
      };
    }
  } else if (area.startsWith("POLYGON")) {
    // POLYGON ((lat1 lon1, lat2 lon2, ...))
    const match = area.match(/POLYGON\s*\(\((.*)\)\)/i);
    if (match) {
      const coords = match[1].split(",").map((pair) => {
        const [lat, lng] = pair.trim().split(/\s+/).map(parseFloat);
        return [lng, lat] as [number, number];
      });
      return { type: "polygon", coordinates: coords };
    }
  }
  return { type: "circle" };
};

const toWKT = (type: GeofenceType, center: [number, number], radius: number, coordinates: [number, number][]): string => {
  if (type === "circle") {
    // CIRCLE (lat lon, radius)
    return `CIRCLE (${center[1]} ${center[0]}, ${radius})`;
  } else {
    // POLYGON ((lat1 lon1, lat2 lon2, ...))
    const coordStr = coordinates.map((c) => `${c[1]} ${c[0]}`).join(", ");
    return `POLYGON ((${coordStr}))`;
  }
};

const Geofences = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null);
  const [deletingGeofence, setDeletingGeofence] = useState<Geofence | null>(null);
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>(null);

  const [formData, setFormData] = useState<GeofenceFormData>({
    name: "",
    description: "",
    type: "circle",
    radius: 500,
    coordinates: [],
    maxSpeed: "",
    color: DEFAULT_COLOR,
  });
  const [drawCenter, setDrawCenter] = useState<[number, number] | null>(null);
  const [drawPolygon, setDrawPolygon] = useState<[number, number][]>([]);

  const { data: geofences = [], isLoading } = useQuery({
    queryKey: ["geofences"],
    queryFn: fetchGeofences,
  });

  const createMutation = useMutation({
    mutationFn: createGeofence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["geofences"] });
      toast.success(t("geofences.created"));
      closeDialog();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateGeofence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["geofences"] });
      toast.success(t("geofences.updated"));
      closeDialog();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteGeofence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["geofences"] });
      toast.success(t("geofences.deleted"));
      setIsDeleteDialogOpen(false);
      setDeletingGeofence(null);
      if (selectedGeofence?.id === deletingGeofence?.id) {
        setSelectedGeofence(null);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingGeofence(null);
    setFormData({ name: "", description: "", type: "circle", radius: 500, coordinates: [], maxSpeed: "", color: DEFAULT_COLOR });
    setDrawCenter(null);
    setDrawPolygon([]);
  };

  const openCreateDialog = (type: GeofenceType) => {
    setFormData({ name: "", description: "", type, radius: 500, coordinates: [], maxSpeed: "", color: DEFAULT_COLOR });
    setDrawCenter(null);
    setDrawPolygon([]);
    setIsDialogOpen(true);
  };

  const openEditDialog = (geofence: Geofence) => {
    const parsed = parseWKT(geofence.area);
    setEditingGeofence(geofence);
    setFormData({
      name: geofence.name,
      description: geofence.description || "",
      type: parsed.type,
      radius: parsed.radius || 500,
      coordinates: parsed.coordinates || [],
      maxSpeed: typeof geofence.attributes.maxSpeed === "number" ? geofence.attributes.maxSpeed : "",
      color: typeof geofence.attributes.color === "string" ? geofence.attributes.color : DEFAULT_COLOR,
    });
    setDrawCenter(parsed.center || null);
    setDrawPolygon(parsed.coordinates || []);
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error(t("geofences.enterName"));
      return;
    }

    if (formData.type === "circle" && !drawCenter) {
      toast.error(t("geofences.setCenter"));
      return;
    }

    if (formData.type === "polygon" && drawPolygon.length < 3) {
      toast.error(t("geofences.minPoints"));
      return;
    }

    const area = toWKT(
      formData.type,
      drawCenter || [0, 0],
      formData.radius,
      formData.type === "polygon" ? [...drawPolygon, drawPolygon[0]] : [] // Close polygon
    );

    const geofenceData = {
      name: formData.name,
      description: formData.description || undefined,
      area,
      attributes: {
        ...(formData.maxSpeed !== "" ? { maxSpeed: formData.maxSpeed } : {}),
        color: formData.color,
      },
    };

    if (editingGeofence) {
      updateMutation.mutate({ ...geofenceData, id: editingGeofence.id });
    } else {
      createMutation.mutate(geofenceData);
    }
  };

  const handleMapClick = (lngLat: [number, number]) => {
    if (formData.type === "circle") {
      setDrawCenter(lngLat);
    } else {
      setDrawPolygon((prev) => [...prev, lngLat]);
    }
  };

  const handleUndoPolygonPoint = () => {
    setDrawPolygon((prev) => prev.slice(0, -1));
  };

  const getGeofenceIcon = (area: string) => {
    if (area.startsWith("CIRCLE")) return <Circle className="h-4 w-4" />;
    if (area.startsWith("POLYGON")) return <Pentagon className="h-4 w-4" />;
    return <MapPin className="h-4 w-4" />;
  };

  const getGeofenceDetails = (geofence: Geofence) => {
    const parsed = parseWKT(geofence.area);
    if (parsed.type === "circle" && parsed.radius) {
      return `${t("geofences.circle")} • ${parsed.radius >= 1000 ? `${(parsed.radius / 1000).toFixed(1)} km` : `${parsed.radius} m`} ${t("geofences.radius")}`;
    }
    if (parsed.type === "polygon" && parsed.coordinates) {
      return `${t("geofences.polygon")} • ${parsed.coordinates.length - 1} ${t("geofences.points")}`;
    }
    return "";
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h1 className="text-2xl font-bold">{t("geofences.title")}</h1>
            <p className="text-muted-foreground">{t("geofences.subtitle")}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openCreateDialog("polygon")}>
              <Pentagon className="h-4 w-4 mr-2" />
              {t("geofences.newPolygon")}
            </Button>
            <Button onClick={() => openCreateDialog("circle")}>
              <Plus className="h-4 w-4 mr-2" />
              {t("geofences.newCircle")}
            </Button>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* Geofence List */}
          <div className="border-r flex flex-col min-h-0">
            <div className="p-3 border-b">
              <Input placeholder={t("geofences.searchPlaceholder")} className="h-9" />
            </div>
            <ScrollArea className="flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : geofences.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <MapPin className="h-12 w-12 text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">{t("geofences.noGeofences")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("geofences.noGeofencesHint")}</p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {geofences.map((geofence) => (
                    <Card
                      key={geofence.id}
                      className={`cursor-pointer transition-colors hover:bg-accent/50 ${
                        selectedGeofence?.id === geofence.id ? "border-primary bg-accent/30" : ""
                      }`}
                      onClick={() => setSelectedGeofence(geofence)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-2 min-w-0">
                            <div
                              className="mt-0.5 shrink-0"
                              style={{ color: typeof geofence.attributes.color === "string" ? geofence.attributes.color : DEFAULT_COLOR }}
                            >
                              {getGeofenceIcon(geofence.area)}
                            </div>
                            <div className="min-w-0 max-w-60">
                              <p className="font-medium truncate">{geofence.name}</p>
                              <p className="text-xs text-muted-foreground">{getGeofenceDetails(geofence)}</p>
                              {typeof geofence.attributes.maxSpeed === "number" && (
                                <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                                  <Gauge className="h-3 w-3" />
                                  <span>{geofence.attributes.maxSpeed} km/h</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <GeofenceDevicesDialog
                              geofence={geofence}
                              trigger={
                                <Button variant="ghost" size="icon" className="h-7 w-7">
                                  <Cpu className="h-3.5 w-3.5" />
                                </Button>
                              }
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => openEditDialog(geofence)}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => {
                                setDeletingGeofence(geofence);
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Map */}
          <div className="flex-1">
            <GeofenceMap
              geofences={geofences}
              selectedGeofence={selectedGeofence}
              onGeofenceSelect={setSelectedGeofence}
            />
          </div>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingGeofence ? t("geofences.editGeofence") : t("geofences.createGeofence")}</DialogTitle>
            <DialogDescription>
              {formData.type === "circle"
                ? t("geofences.clickToSetCenter")
                : t("geofences.clickToAddPoints")}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("geofences.name")}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t("geofences.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t("geofences.description")}</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder={t("geofences.descriptionPlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxSpeed">{t("geofences.maxSpeed")}</Label>
                <Input
                  id="maxSpeed"
                  type="number"
                  min={1}
                  max={300}
                  value={formData.maxSpeed}
                  onChange={(e) => setFormData((prev) => ({ ...prev, maxSpeed: e.target.value === "" ? "" : parseInt(e.target.value) || "" }))}
                  placeholder={t("geofences.maxSpeedPlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="color">{t("geofences.color")}</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                    className="h-8 w-12 cursor-pointer rounded border border-input bg-transparent p-0.5"
                  />
                  <span className="text-sm text-muted-foreground font-mono">{formData.color}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("geofences.type")}</Label>
                <Badge variant="secondary" className="gap-1">
                  {formData.type === "circle" ? <Circle className="h-3 w-3" /> : <Pentagon className="h-3 w-3" />}
                  {formData.type === "circle" ? t("geofences.circle") : t("geofences.polygon")}
                </Badge>
              </div>
              {formData.type === "circle" && (
                <div className="space-y-2">
                  <Label htmlFor="radius">{t("geofences.radiusMeters")}</Label>
                  <Input
                    id="radius"
                    type="number"
                    min={10}
                    max={100000}
                    value={formData.radius}
                    onChange={(e) => setFormData((prev) => ({ ...prev, radius: parseInt(e.target.value) || 500 }))}
                  />
                </div>
              )}
              {formData.type === "polygon" && drawPolygon.length > 0 && (
                <div className="space-y-2">
                  <Label>{t("geofences.polygonPoints")}: {drawPolygon.length}</Label>
                  <Button variant="outline" size="sm" onClick={handleUndoPolygonPoint}>
                    {t("geofences.undoLastPoint")}
                  </Button>
                </div>
              )}
            </div>

            <div className="col-span-2 h-[400px] rounded-lg overflow-hidden border">
              <GeofenceMap
                geofences={editingGeofence ? [editingGeofence] : []}
                selectedGeofence={editingGeofence}
                isDrawing
                drawType={formData.type}
                drawCenter={drawCenter}
                drawRadius={formData.radius}
                drawPolygon={drawPolygon}
                drawColor={formData.color}
                onMapClick={handleMapClick}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingGeofence ? t("common.update") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("geofences.deleteGeofence")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("geofences.deleteConfirm", { name: deletingGeofence?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingGeofence && deleteMutation.mutate(deletingGeofence.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Geofences;
